/* ═══════════════════════════════════════════════════════════════
   REVIEW — pure summaries of the master-updates feed (SYNC_PLAN §2.6).
   The Master updates widget and the Specs widget's per-section badge read
   the same numbers the Review bar does.
   ═══════════════════════════════════════════════════════════════ */

import type { SyncFeed, SyncFeedRow } from '../services/syncService';

/** Changes still waiting on a decision, across pending rows. */
export function pendingChangeCount(rows: Array<Pick<SyncFeedRow, 'status' | 'changes'>>): number {
  let n = 0;
  for (const r of rows) {
    if (r.status !== 'pending') continue;
    for (const c of r.changes) if (c.kind !== 'reconciled' && !c.decision) n++;
  }
  return n;
}

/** "from 09 91 23 Painting master" / "from <module name>" / "from <variant>". */
export function upstreamText(
  row: Pick<SyncFeedRow, 'upstreamKind' | 'upstreamLabel' | 'moduleName'>
): string {
  if (row.upstreamKind === 'master') {
    return row.upstreamLabel ? `from ${row.upstreamLabel} master` : 'from its master';
  }
  if (row.upstreamKind === 'module') {
    return `from ${row.moduleName || row.upstreamLabel || 'a module'}`;
  }
  return `from ${row.upstreamLabel || 'its client version'}`;
}

export interface SectionUpdates {
  specId: string;
  /** Changes waiting on a decision across this section's pending rows. */
  count: number;
  /** One text per pending row ("from …"). */
  from: string[];
  /** Variants this section is still waiting on ("waiting upstream at …"). */
  waitingAt: string[];
}

/** One entry per project spec that has anything pending, fed rows grouped by
 *  copy. Only `project_spec` rows count here (forma rows ride their own
 *  badge). Sections with nothing to act on or wait for are left out. */
export function summarizeBySpec(feed: SyncFeed | null): Map<string, SectionUpdates> {
  const out = new Map<string, SectionUpdates>();
  if (!feed) return out;
  const entry = (specId: string): SectionUpdates => {
    let e = out.get(specId);
    if (!e) {
      e = { specId, count: 0, from: [], waitingAt: [] };
      out.set(specId, e);
    }
    return e;
  };
  for (const r of feed.rows) {
    if (r.status !== 'pending' || r.targetKind !== 'project_spec') continue;
    const n = pendingChangeCount([r]);
    if (n === 0) continue;
    const e = entry(r.targetId);
    e.count += n;
    e.from.push(upstreamText(r));
  }
  for (const u of feed.upstreamPending) entry(u.targetId).waitingAt.push(u.label);
  return out;
}

/** Pending project-forma changes (the forma branch's rows ride the same
 *  feed; the Specs widget header shows one badge for them). */
export function pendingFormaCount(feed: SyncFeed | null): number {
  if (!feed) return 0;
  return pendingChangeCount(feed.rows.filter((r) => r.targetKind === 'project_forma'));
}
